import { channels as mockChannels, getChannel, type Channel } from '../data/mock';

const BFF_BASE = import.meta.env.VITE_BFF_URL ?? 'https://api.provny.com';

/**
 * A channel as returned by the Provny BFF — a separate service from MemeGenes,
 * with its own base URL. snake_case over the wire.
 */
type ChannelDto = {
  slug: string;
  name: string;
  tagline: string;
  description: string;
  hero_image_url: string;
  follower_count: number;
  originals_count: number;
  status: string;
  created_at: string;
};

function mapChannel(dto: ChannelDto): Channel {
  return {
    slug: dto.slug,
    name: dto.name,
    tagline: dto.tagline,
    image: dto.hero_image_url,
    followers: dto.follower_count,
    originals: dto.originals_count,
    dropsCount: 0, // BFF has no drops yet — Phase 1 channels only
  };
}

/**
 * Fetch the active channels from the Provny BFF. Fail-open: on any
 * network/HTTP/parse error, returns the mock `channels` so the channel
 * surfaces stay populated when the BFF is unreachable.
 */
export async function fetchChannels(): Promise<Channel[]> {
  try {
    const res = await fetch(`${BFF_BASE}/channels?status=active`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as { channels?: ChannelDto[] };
    if (!Array.isArray(data.channels)) throw new Error('malformed response');
    return data.channels.map(mapChannel);
  } catch (err) {
    console.warn('fetchChannels: falling back to mock channels', err);
    return mockChannels;
  }
}

/**
 * Fetch a single channel by slug from the Provny BFF. Returns `null` on 404.
 * On any other network/HTTP/parse error, falls back to the mock channel (or
 * `null` if no mock exists).
 */
export async function fetchChannel(slug: string): Promise<Channel | null> {
  try {
    const res = await fetch(`${BFF_BASE}/channels/${encodeURIComponent(slug)}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const dto = (await res.json()) as ChannelDto;
    return mapChannel(dto);
  } catch (err) {
    console.warn(`fetchChannel(${slug}): falling back to mock channel`, err);
    return getChannel(slug) ?? null;
  }
}
